import { useCallback, useEffect, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import { isNewStoreFurtherOrEqual } from 'features/store/components/auto-select-closest-store-prompt/utils';
import { DEFAULT_SEARCH_RADIUS_IN_METERS } from 'features/store/constants';
import { useAutoSelectStoreUtils } from 'features/store/hooks/use-auto-select-store-utils';
import { useNearbyRestaurants } from 'features/store/pages/pickup-store-selector/hooks/helpers/use-restaurants';

import { IRestaurant } from '@rbi-ctg/store';
import { OperationalStatus, ServiceMode } from 'generated/graphql-gateway';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeLive } from 'state/service-mode';
import { coordinateDistance } from 'utils/rbi-common';

import {
  useEnableCloserStoreCoachmarkExperiment,
  useExposeCloserStoreCoachmarkExperiment,
} from './use-closer-store-coachmark-experiment';

/** Session latch so remounting checkout does not re-show after X. */
let dismissedCloserRestaurantCoachmarkThisSession = false;

const sortRestaurantsByDistance = (
  restaurants: IRestaurant[],
  userLat: number,
  userLng: number
): IRestaurant[] => {
  const userCoords = { latitude: userLat, longitude: userLng };
  return [...restaurants].sort((a, b) => {
    if (!a.latitude || !a.longitude) {
      return 1;
    }
    if (!b.latitude || !b.longitude) {
      return -1;
    }
    return (
      coordinateDistance(userCoords, { latitude: a.latitude, longitude: a.longitude }) -
      coordinateDistance(userCoords, { latitude: b.latitude, longitude: b.longitude })
    );
  });
};

export type UseCloserRestaurantCoachmarkReturn = {
  isVisible: boolean;
  dismiss: () => void;
  /** Human-readable distance to the guest's selected store (e.g. "3.5 miles"), for the coachmark copy. */
  distanceText: string;
};

/**
 * Evaluates whether checkout should show the non-blocking closer-restaurant coachmark.
 * Returns visibility + dismiss callback + distance copy (no JSX).
 */
export const useCloserRestaurantCoachmark = (): UseCloserRestaurantCoachmarkReturn => {
  const isCoachmarkExperimentEnabled = useEnableCloserStoreCoachmarkExperiment();
  const serviceMode = useServiceModeLive();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const { store: activeStore, noStoreSelected } = useStoreContext();
  const { isPermissionGranted, userCoordinates } = useGeolocation();
  const { fetch: fetchNearbyStores } = useNearbyRestaurants();
  const { getFirstAvailableStore } = useAutoSelectStoreUtils();
  // Distance to the store the guest already has selected (and is about to order from) - not
  // the closer candidate being suggested. Mirrors how the store-locator widget itself computes
  // the selected store's distance (see useStoreLocatorWithMap).
  const { distanceText } = useDistanceText({
    restaurant: activeStore,
    shouldUseUserCoordinatesIfAvailable: true,
  });
  const defaultDiagonal =
    useFlag(LaunchDarklyFlag.RADIUS_FOR_STORE_LOCATOR) || DEFAULT_SEARCH_RADIUS_IN_METERS;

  // Candidate is keyed to the store/coords that produced it, so a selection or location
  // change hides the coachmark immediately without a stale true flag during re-fetch.
  const [closerCandidateKey, setCloserCandidateKey] = useState<string | null>(null);
  // Tracks which eligibilityKey the nearby-store evaluation has *finished* resolving for,
  // regardless of outcome - distinct from closerCandidateKey, which is only set when a closer
  // store was actually found. Needed so exposure can wait for a real true/false answer instead
  // of firing before eligibility is known.
  const [evaluatedKey, setEvaluatedKey] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(dismissedCloserRestaurantCoachmarkThisSession);
  const hasLoggedViewRef = useRef(false);
  const requestIdRef = useRef(0);

  const eligibilityKey =
    activeStore?._id && userCoordinates
      ? `${activeStore._id}:${userCoordinates.lat.toFixed(3)}:${userCoordinates.lng.toFixed(3)}`
      : null;
  const hasCloserRestaurant = closerCandidateKey !== null && closerCandidateKey === eligibilityKey;

  // Structural preconditions independent of the experiment arm - a control user can be
  // "eligible" for exposure purposes the same way a treatment user can, they just never see
  // the coachmark UI itself.
  const meetsStructuralPreconditions =
    !isDelivery && !noStoreSelected && isPermissionGranted && !!userCoordinates && !!eligibilityKey;
  const isEligibilityResolved = !meetsStructuralPreconditions || evaluatedKey === eligibilityKey;
  const isEligible = meetsStructuralPreconditions && hasCloserRestaurant;
  // undefined while the closer-store evaluation is still pending, so exposure can wait for a
  // real answer instead of treating a mid-evaluation false negative as final.
  const resolvedIsEligible = isEligibilityResolved ? isEligible : undefined;

  const isVisible = isCoachmarkExperimentEnabled && isEligible && !isDismissed;

  // Log experiment exposure only for guests actually eligible for the coachmark, for both
  // treatment and control - see useExposeCloserStoreCoachmarkExperiment for why exposure must
  // reflect real eligibility rather than firing unconditionally on checkout mount.
  useExposeCloserStoreCoachmarkExperiment(resolvedIsEligible);

  useEffect(() => {
    if (!meetsStructuralPreconditions || !eligibilityKey) {
      setCloserCandidateKey(null);
      setEvaluatedKey(null);
      return;
    }

    if (isDismissed || dismissedCloserRestaurantCoachmarkThisSession) {
      // Dismiss only hides the coachmark UI - eligibility was already resolved before the
      // guest could dismiss it, so leave evaluatedKey/closerCandidateKey alone rather than
      // re-triggering an evaluation or flipping isEligibilityResolved back to unresolved.
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;
    const evaluationKey = eligibilityKey;

    const evaluate = async () => {
      const resolve = (foundCloserStore: IRestaurant | false) => {
        setCloserCandidateKey(foundCloserStore ? evaluationKey : null);
        setEvaluatedKey(evaluationKey);
      };

      try {
        const nearbyStores = await fetchNearbyStores(
          {
            first: 10,
            status: OperationalStatus.OPEN,
            radiusStrictMode: true,
            coordinates: {
              searchRadius: defaultDiagonal,
              userLat: Number(userCoordinates.lat.toFixed(3)),
              userLng: Number(userCoordinates.lng.toFixed(3)),
            },
          },
          { allowFromCache: false }
        );

        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        if (!nearbyStores?.length || !activeStore?._id) {
          resolve(false);
          return;
        }

        // Fail closed when the active store lacks coordinates for a fair comparison.
        if (!activeStore.latitude || !activeStore.longitude) {
          resolve(false);
          return;
        }

        const sorted = sortRestaurantsByDistance(
          nearbyStores as IRestaurant[],
          userCoordinates.lat,
          userCoordinates.lng
        );
        const firstAvailableStore = await getFirstAvailableStore(sorted);

        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        if (!firstAvailableStore || firstAvailableStore._id === activeStore._id) {
          resolve(false);
          return;
        }

        // Incomplete candidate coords → treat as not closer (fail closed for coachmark).
        if (!firstAvailableStore.latitude || !firstAvailableStore.longitude) {
          resolve(false);
          return;
        }

        if (isNewStoreFurtherOrEqual(userCoordinates, firstAvailableStore, activeStore)) {
          resolve(false);
          return;
        }

        resolve(firstAvailableStore);
      } catch {
        if (!cancelled && requestId === requestIdRef.current) {
          resolve(false);
        }
      }
    };

    void evaluate();

    return () => {
      cancelled = true;
    };
  }, [
    activeStore,
    defaultDiagonal,
    eligibilityKey,
    meetsStructuralPreconditions,
    fetchNearbyStores,
    getFirstAvailableStore,
    isDismissed,
    userCoordinates,
  ]);

  useEffect(() => {
    if (!isVisible) {
      hasLoggedViewRef.current = false;
      return;
    }

    if (hasLoggedViewRef.current) {
      return;
    }

    hasLoggedViewRef.current = true;
    logEvent(CustomEventNames.CLOSER_RESTAURANT_COACHMARK_VIEWED, EventTypes.Other, {
      selectedStoreId: activeStore?._id,
    });
  }, [activeStore?._id, isVisible]);

  const dismiss = useCallback(() => {
    dismissedCloserRestaurantCoachmarkThisSession = true;
    setIsDismissed(true);
    setCloserCandidateKey(null);
    logEvent(CustomEventNames.CLOSER_RESTAURANT_COACHMARK_DISMISSED, EventTypes.Other, {
      selectedStoreId: activeStore?._id,
    });
  }, [activeStore?._id]);

  return { isVisible, dismiss, distanceText };
};

/** Test helper — reset session latch between Jest cases. */
export const resetCloserRestaurantCoachmarkSessionForTests = (): void => {
  dismissedCloserRestaurantCoachmarkThisSession = false;
};
